//@flow
import FunctionStore from '../Base/functionStoreClass';
import Constants from '../../constants';
import { updateState , applyServerDefaultData } from '../Base/storeUtils';
import { toJS, toImmutable } from '../../Utils/immutable';
import qs from 'qs';
import GlobalData from '../../Utils/globalData';

import {
  ConfigItemType,
  ActivationConditionStateValueName,
  ActivationConditionType,
  ActivationConditionMatchType,
} from './mktgModelInterfaces';

import type { HandlerAdd, StoreState } from '../Base/functionStoreClass';

import type {
  ActivationConfigParam,
  BannerConfig,
  HelloBarConfig,
  ConfigItem,
  ConfigItemsState,
  HelloBarMeta,
  HelloBarStyles,
} from './mktgModelInterfaces';

const BASE_IMAGE_URL = GlobalData.get('mktgConfig', 'assetBucketUrl') || '';

export const getDefaultHelloBarStyles = () => {
  return {color: '', backgroundColor: ''};
};

export const getDefaultHelloBarMeta = () => {
  return {
    styles: getDefaultHelloBarStyles(),
  };
};

export const parseHelloBarMeta = (rawMeta?:string):HelloBarMeta => {
  if (!rawMeta) {
    return getDefaultHelloBarMeta();
  }
  try {
    const meta = JSON.parse(rawMeta);
    if (meta) {
      return meta;
    } else {
      return getDefaultHelloBarMeta();
    }
  } catch(e) {
    return getDefaultHelloBarMeta();
  }
};

export const parseStylesFromHelloBarMeta = (rawMeta?:string):HelloBarStyles => {
  const meta = parseHelloBarMeta(rawMeta);
  if (meta && meta.styles) {
    return meta.styles;
  }
  else {
    return getDefaultHelloBarStyles();
  }
};

export const parseBannerUiConfigStyles = (styles?:string):Object => {
  if (!styles) return {};
  try {
    return JSON.parse(styles);
  } catch(e) {
    return {};
  }
};

// AppStateData can be passed into getters
// (e.g. `getBannerConfig`) so that MktgStore
// has access to data from other stores and external sources.
// Currently this is just used for knowing if current user
// is a member
export type AppStateDataRaw = {
  isMember?:boolean,
  isLoggedIn?:boolean,
  path?:string,
  query?:string,
  host?:string,
  timestamp?:number,
};

export type AppStateData = {
  isMember?:boolean,
  isLoggedIn?:boolean,
  path?:string,
  query?:Object,
  host?:string,
  timestamp?:number,
};

export default class MktgStore extends FunctionStore {
  _initialState(state:StoreState) {
    applyServerDefaultData('mktgData', state, _receivePreloadedMktgData);
  }

  getMktgData() {
    const mktgData = this.getState().get('mktgData');
    if (!mktgData) return null;
    return toJS(mktgData);
  }

  getConfigItems(stateData:AppStateDataRaw = {}) {
    const mktgData = this.getMktgData();
    if (!(mktgData && mktgData.configItems)) return [];
    const path = stateData.path || window.location.pathname;
    const rawQuery = stateData.query || window.location.search;
    const query = qs.parse(rawQuery, {ignoreQueryPrefix: true});
    const host = stateData.host || window.location.host;
    const nextStateData = {
      path,
      query,
      host,
      isMember: stateData.isMember,
      isLoggedIn: stateData.isLoggedIn,
      timestamp: stateData.timestamp,
    };
    return _getConfigItems(mktgData.configItems, nextStateData);
  }

  getBannerConfig(stateData:AppStateDataRaw = {}):?BannerConfig {
    const configItems = this.getConfigItems(stateData);
    const configItem = configItems.find(configItem => configItem.type === ConfigItemType.BANNER);
    if (configItem) {
      return (configItem.bannerConfig:BannerConfig);
    }
    return undefined;
  }

  getHelloBarConfig(stateData:AppStateDataRaw = {}):HelloBarConfig {
    const configItems = this.getConfigItems(stateData);
    const configItem = configItems.find(configItem => configItem.type === ConfigItemType.HELLO_BAR);
    if (configItem) {
      return (configItem.helloBarConfig:HelloBarConfig);
    }
    return defaultHelloBarConfig;
  }

  getFreeGiftConfig(stateData:AppStateDataRaw = {}) {
    const configItems = this.getConfigItems(stateData);
    // TODO: currently the only `free_gift_update_ux` is
    // not currently available in config items data set
    const configItem = configItems.find(configItem => configItem.type === 'free_gift_update_ux');
    if (configItem) {
      return configItem.meta.uiConfig.UXData;
    }
    return defaultFreeGiftConfig;
  }

  _setup(onSuccess:HandlerAdd, onError:HandlerAdd) {
    onSuccess(Constants.GET_MKTG_DATA_SUCCESS, updateState(_receiveMktgData));
  }
}

function _receiveMktgData(state:StoreState, data:{configItems:ConfigItem[]}) {
  const configItemsObject = {};
  data.configItems.forEach(configItem => {
    configItemsObject[configItem._id] = configItem;
  });
  state.set('mktgData', toImmutable({configItems: configItemsObject}));
}

function _receivePreloadedMktgData(state:StoreState, data:{configItems:ConfigItem[]}) {
  const configItemsObject = {};
  data.configItems.forEach(configItem => {
    configItemsObject[configItem._id] = configItem;
  });
  state.set('mktgData', toImmutable({configItems: configItemsObject}));
}


const checkValues = (v1:any, v2:any, matchType?:typeof ActivationConditionMatchType):boolean => {
  if (matchType === ActivationConditionMatchType.EXACT) {
    return v1 === v2;
  }
  else if (matchType === ActivationConditionMatchType.STARTS_WITH) {
    return (v1 || '').startsWith(v2);
  }
  else if (matchType === ActivationConditionMatchType.NOT_EQUAL) {
    return v1 !== v2;
  }
  else {
    // Default to equality check if unknown or no
    // matchType provided
    return v1 === v2;
  }
};

const checkCondition = (c, stateData):boolean => {
  const {
    path,
    query = {},
    host,
    isMember = false,
    isLoggedIn = false,
    timestamp,
  } = stateData;

  const matchType = c.match;

  if (c.type === ActivationConditionType.QUERY) {
    return checkValues(query[c.name || ''], c.value, matchType);
  } else if (c.type === ActivationConditionType.PATH) {
    return checkValues(path, c.value, matchType);
  } else if (c.type === ActivationConditionType.HOST) {
    return checkValues(host, c.value, matchType);
  } else if (c.type === ActivationConditionType.TIME) {
    if (!(c.start && c.end)) return false;
    const now = timestamp || new Date().getTime();
    const start = new Date(c.start || '').getTime();
    const end = new Date(c.end || '').getTime();
    return now >= start && now <= end;
  } else if (c.type === ActivationConditionType.STATE_VALUE) {
    if (c.name === ActivationConditionStateValueName.IS_MEMBER) {
      return checkValues(isMember.toString(), c.value, matchType);
    } if (c.name === ActivationConditionStateValueName.IS_LOGGED_IN) {
      return checkValues(isLoggedIn.toString(), c.value, matchType);
    } else {
      return false;
    }
  } else {
    return false;
  }
};

const checkActivationConfigParam = (param:ActivationConfigParam, stateData):boolean => {
  const {operator, conditions} = param;
  if (operator === 'or') {
    return conditions.some(c => checkCondition(c, stateData));
  } else if (operator === 'and') {
    return conditions.every(c => checkCondition(c, stateData));
  } else {
    return false;
  }
};

const _getConfigItems = (configItemsObject:ConfigItemsState, stateData:AppStateData) => {
  const filtered = Object.values(configItemsObject).filter((configItem:any) => {
    const {params, operator} = configItem.activationConfig;
    // If activation config params array is empty, this action
    // should always be applied
    if (params.length === 0) return true;
    if (operator === 'and') {
      return params.every(param => checkActivationConfigParam(param, stateData));
    } else if (operator === 'or') {
      return params.some(param => checkActivationConfigParam(param, stateData));
    } else {
      return false;
    }
  });
  const grouped = {};
  // Group by type, then sort by priority.
  // This effectively allows multiple actions
  // of different types to run simultaneously.
  filtered.forEach((configItem:any) => {
    if (grouped[configItem.type]) {
      grouped[configItem.type].push(configItem);
    }
    else {
      grouped[configItem.type] = [configItem];
    }
  });
  const uniqueConfigItems = [];

  Object.keys(grouped).forEach(type => {
    const configItems = grouped[type];
    configItems.sort((el1, el2) => {
      if (el1.priority < el2.priority) return 1;
      else if (el1.priority > el2.priority) return -1;
      else return 0;
    });
    // After sort, take first element
    uniqueConfigItems.push(configItems[0]);
  });
  return uniqueConfigItems;
};

export const getImageUrl = (url?:?string) => {
  if (!url) return '';
  return url.startsWith('/')
    ? `${BASE_IMAGE_URL}${url}`
    : `${BASE_IMAGE_URL}/${url}`;
};

export const defaultHelloBarConfig = {
  text: 'Snack boxes for teams, meetings, events!',
  link: 'https://naturebox.com/office',
};

export const defaultFreeGiftConfig = {
  hero: {
    copies: {
      titleLn1: '',
      titleLn2: '',
      subtitle: '',
      bullets: [],
      button: '',
    },

    images: {
      small: '',
      medium: '',
      large: '',
      xlarge: '',
    },
  },

  freeGiftModal: {
    copies: {
      title: '',
      bullets: [],
      button: '',
    },
  },

  howItWorks: {
    copies: {},
  },
};
